import { Injectable } from '@angular/core';
import {
  PatientSimulation,
  ClinicBrief, PatientData, ClinicPublicInfo, ML_STATUS,
} from '../common.interface';
import { BehaviorSubject, Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import { ApiService } from './api.service';
import {filter, map, tap} from 'rxjs/operators';
import { ALLOWED_SIMS_FOR_PATIENT, Roles, USER_LS_KEY } from '../common.constants';


@Injectable({providedIn: 'root'})
export class UsersDataService {
  private patient: PatientData;
  private selectedSimulationSubj$ = new BehaviorSubject<PatientSimulation>(null);
  selectedSimulation$ = this.selectedSimulationSubj$.asObservable().pipe(
    filter(s => !!s)
  );

  newConsultation = false; // Impact on redirect Route after capturing process and Instructions state

  private loggedClinicRefCode: string;
  private loggedClinicData: ClinicBrief;
  private loggedClinicPublicData: ClinicPublicInfo; // goes from capturing

  userId;
  patientId;

  deviceId: string; // is generated on first image uploading. Used for connection uploaded image and submitted form

  config;
  baseData;
  loggedRole;

  constructor(private api: ApiService) {
  }

  // used after log-out
  clearClinicData() {
    this.loggedClinicData = null;
    this.loggedClinicRefCode = null;
  }

  setLoggedClinicPublicData(clinic: ClinicPublicInfo) {
    this.loggedClinicPublicData = clinic;
  }

  get getLoggedClinicPublicData() {
    return this.loggedClinicPublicData;
  }

  setLoggedClinicData(clinic: ClinicBrief) {
    this.loggedClinicData = clinic;
    this.loggedClinicRefCode = clinic.ref_code;
  }

  get getLoggedClinicData() {
    return this.loggedClinicData || {} as ClinicBrief;
  }

  get getLoggedClinicRef() {
    return this.loggedClinicRefCode;
  }

  setLoggedClinicRefOnly(ref: string) {
    this.loggedClinicRefCode = ref;
  }

  setUserRole(role: number) {
    this.loggedRole = role;
    console.log('this.loggedRole', this.loggedRole)
  }

  get clinicLegalTermsLink(): string {
    return this.loggedClinicPublicData?.legalterms_url || environment.legalTermsLink;
  }

  canCreateSimulation(): boolean {
    let loggedRole = this.loggedRole;
    if (!loggedRole) {
      const userData = JSON.parse(localStorage.getItem(USER_LS_KEY()));
      loggedRole = userData?.role;
    }

    if (!this.patient || (loggedRole && loggedRole > Roles.CLINIC_USER)) {
      return true;
    }
    return this.patient.simulations.length < ALLOWED_SIMS_FOR_PATIENT;
  }

  getUserData() {
    return {
      clinic: this.loggedClinicData?.id || this.loggedClinicPublicData?.id,
      device_id: this.getDeviceId,
    };
  }

  public get getDeviceId(): string {
    if (this.deviceId) {
      return this.deviceId;
    }
    this.deviceId = Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
    return this.deviceId;
  }

  get patientData() {
    return this.patient;
  }

  set patientData(data: PatientData) {
    this.patient = data;
  }

  getCurrentPatientData(id = null): Observable<PatientData> {
    const refCode = id || this.patientData.refCode;
    const opt = {
      url: `${environment.apiUrls.patient}${refCode}/`,
      withSpinner: false,
    };
    return this.api.get(opt).pipe(
      map(data => {
        data.simulations.forEach(simulation => {
          const imagesHD = simulation.images_ai_hd;
          const imagesLD = simulation.images_ai_ld;
          if (!imagesHD || !imagesLD) {
            simulation.imagesMLv2 = [];
          } else {
            const imagesMLv2 = Object.entries(imagesLD)
              .reduce((store: string[][], [key, imageLDUrl]) => {
                const imageHDUrl = imagesHD[key];
                if (imageLDUrl && imageHDUrl) {
                  store.push([imageLDUrl, imageHDUrl]);
                }
                return store;
              }, []);
            simulation.imagesMLv2 = imagesMLv2 as string[][];
          }
        });
        return data;
      })
    );
  }

  updatePatientData(id: string): Observable<PatientData> {
    return this.getCurrentPatientData(id).pipe(
      tap((data: PatientData) => {
        this.patientData = data;
      })
    );
  }

  set selectedSimulation(simulation: PatientSimulation) {
    this.selectedSimulationSubj$.next(simulation);
  }

  get selectedSimulation() {
    return this.selectedSimulationSubj$.getValue();
  }
}
